import {
  useEffect,
  useState,
  type ComponentType,
  type ContextType
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import {
  useNavigate,
  useOutletContext,
  useParams
} from 'react-router-dom';
import type { FormikHelpers } from 'formik';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import CancelModal from '@/app/common/components/CancelModal';
import AiInsufficientBalanceModal, {
  AI_WALLET_INSUFFICIENT_CODE
} from '@/app/common/components/AiInsufficientBalanceModal';
import AiFormWarningAlert from '@/app/common/components/AiFormWarningAlert';
import AiNoActiveApiKeyAlert from '@/app/common/components/AiNoActiveApiKeyAlert';
import {
  useGetAiKeywordsHistoryQuery,
  useSendAiKeywordResearchMutation,
  useDeleteAiKeywordsHistoryMutation
} from '@/app/KeywordResearch/api/aiKeywordsApi';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  AI_NO_ACTIVE_PROVIDER_KEY_CODE,
  resolveAiFormAlertMessage
} from '@/app/common/validators/aiMessages';
import { aiKeywordsHistoryColumns } from '../config/aiKeywordsHistoryColumnConfig';
import { RemoveAiKeywordsContext } from '@/context/Context';
import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';
import AiKeywordsForm from '@/app/KeywordResearch/components/keyword-ai/AiKeywordsForm';
import walletApi from '@/app/Wallet/api/walletApi';
import type { AiKeywordResearchType } from '@/app/KeywordResearch/types/aiKeywordsApiTypes';

type BreadcrumbItem = { text: string; href: string };

type CountryOption = {
  value: string;
  label: string;
  flag?: string;
};

type LanguageOption = {
  value: string;
  label: string;
};

type AiKeywordsFormValues = {
  subject: string;
  goal: string;
  keywords: string;
  type: { value: string; label: string }[];
};

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type AgGridWrapperProps = {
  height: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const AiKeywords = () => {
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { domain_slug = '', domain = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [remId, setRemId] = useState('');
  const [remSubject, setRemSubject] = useState('');
  const [showRem, setShowRem] = useState(false);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] =
    useState(false);
  const [showNoActiveApiKeyAlert, setShowNoActiveApiKeyAlert] = useState(false);
  const [formWarningMessage, setFormWarningMessage] = useState<string | null>(
    null
  );

  const [geoLocationsCountries, setGeoLocationsCountries] = useState<
    CountryOption[]
  >([]);
  const [geoLanguages, setGeoLanguages] = useState<LanguageOption[]>([]);

  const [selectedPlace, setSelectedPlace] = useState<CountryOption>({
    value: 'ir',
    label: 'ایران',
    flag: `https://static.mizfa.tools/flags/ir.webp`
  });

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>({
    value: 'fa',
    label: 'فارسی'
  });

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات AI', href: '#' }]);
  }, [setBreadcrumbData]);

  useEffect(() => {
    if (geoLocationsData) {
      const geoCountries: CountryOption[] = [];
      geoLocationsData.data.map(({ code, fa_name }) =>
        geoCountries.push({
          value: code,
          label: fa_name,
          flag: `https://static.mizfa.tools/flags/${code}.webp`
        })
      );
      setGeoLocationsCountries(geoCountries);
    }
  }, []);

  useEffect(() => {
    if (geoLanguagesData) {
      const nextLanguages: LanguageOption[] = [];
      geoLanguagesData.data.map(({ code, fa_name }) =>
        nextLanguages.push({
          value: code,
          label: fa_name
        })
      );
      setGeoLanguages(nextLanguages);
    }
  }, []);

  const { data: historyData, isLoading: isLoadingHistory } =
    useGetAiKeywordsHistoryQuery();

  const [sendAiKeywordResearch, { isLoading: isSubmitting }] =
    useSendAiKeywordResearchMutation();
  const [deleteHistoryRecord] = useDeleteAiKeywordsHistoryMutation();

  const removeAiKeywordsContextValue = {
    id: remId,
    setId: setRemId,
    subject: remSubject,
    setSubject: setRemSubject,
    show: showRem,
    setShow: setShowRem
  } as ContextType<typeof RemoveAiKeywordsContext>;

  const onSubmit = async (
    values: AiKeywordsFormValues,
    actions: FormikHelpers<AiKeywordsFormValues>
  ) => {
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);
    try {
      const keywordsArray = values.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const typeArray = (values.type
        ? values.type.map(t => t.value)
        : []) as AiKeywordResearchType[];

      const res = await sendAiKeywordResearch({
        domain_slug,
        subject: values.subject.trim(),
        content: values.goal || '',
        keywords: keywordsArray,
        type: typeArray,
        language: selectedLanguage.value,
        country: selectedPlace.value
      }).unwrap();
      if (res?.code === 200 && res?.data?.id) {
        successMessage('درخواست تحقیق کلمات با موفقیت ثبت شد');
        navigate(
          `/keyword-research/ai-keywords/details/${res.data.id}/${domain_slug}/${domain}`
        );
        actions.resetForm();
        setSelectedPlace({
          value: 'ir',
          label: 'ایران',
          flag: `https://static.mizfa.tools/flags/ir.webp`
        });
        setSelectedLanguage({
          value: 'fa',
          label: 'فارسی'
        });
        dispatch(walletApi.util.invalidateTags(['Wallet']));
        return;
      } else {
        if (res?.code === AI_WALLET_INSUFFICIENT_CODE) {
          setShowInsufficientBalanceModal(true);
          return;
        }
        if (res?.code === AI_NO_ACTIVE_PROVIDER_KEY_CODE) {
          setShowNoActiveApiKeyAlert(true);
          return;
        }
        setFormWarningMessage(
          resolveAiFormAlertMessage(
            res?.code,
            res?.msg,
            'در ارسال درخواست تحقیق کلمات AI خطایی رخ داد.'
          )
        );
      }
    } catch (err: unknown) {
      const e = err as {
        status?: number;
        data?: { code?: number; msg?: string };
      };
      if (e?.data?.code === AI_WALLET_INSUFFICIENT_CODE) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      if (e?.data?.code === AI_NO_ACTIVE_PROVIDER_KEY_CODE) {
        setShowNoActiveApiKeyAlert(true);
        return;
      }
      if (e?.status === 500 && e?.data?.code === 0) {
        return;
      }
      setFormWarningMessage(
        resolveAiFormAlertMessage(
          e?.data?.code,
          e?.data?.msg,
          'در ارتباط با سرویس تحقیق کلمات AI خطایی رخ داد. لطفا دوباره تلاش کنید.'
        )
      );
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleDelete = () => {
    deleteHistoryRecord({ id: remId })
      .unwrap()
      .then(res => {
        const r = res as { code?: number; msg?: string };
        if (r?.code === 200) {
          successMessage('حذف تاریخچه با موفقیت انجام شد.');
        } else {
          errorMessage(r?.msg || 'در حذف تاریخچه خطایی رخ داد');
        }
      })
      .catch(() => {
        errorMessage('در حذف تاریخچه خطایی رخ داد');
      });
  };

  return (
    <>
      <AiKeywordsForm
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          geoLanguages={geoLanguages}
          selectedPlace={selectedPlace}
          setSelectedPlace={setSelectedPlace}
          geoLocationsCountries={geoLocationsCountries}
        />
      <AiNoActiveApiKeyAlert
        show={showNoActiveApiKeyAlert}
        onClose={() => setShowNoActiveApiKeyAlert(false)}
      />
      <AiFormWarningAlert
        message={formWarningMessage}
        onClose={() => setFormWarningMessage(null)}
      />

        <Row>
          <Col className="for-style">
            <RemoveAiKeywordsContext.Provider
              value={removeAiKeywordsContextValue}
            >
              {!isLoadingHistory ? (
                <TypedAgGridWrapper
                  height="600px"
                  columns={aiKeywordsHistoryColumns(
                    domain_slug,
                    domain,
                    geoLocationsData.data,
                    geoLanguagesData.data
                  )}
                  data={historyData || []}
                  total={historyData ? historyData?.length : 0}
                  settings={{ pagination: true }}
                />
              ) : (
                <AgGridSkeleton height="600px" />
              )}
            </RemoveAiKeywordsContext.Provider>
          </Col>
        </Row>
        <CancelModal
          setShow={setShowRem}
          show={showRem}
          submitTitle="آره، حذف بشه"
          title="حذف تحقیق کلمات AI"
          submitFunc={handleDelete}
          desc={`آیا مطمئن هستید می‌خواهید تحقیق مربوط به "${remSubject}" را حذف کنید؟`}
        />
        <AiInsufficientBalanceModal
          show={showInsufficientBalanceModal}
          onHide={() => setShowInsufficientBalanceModal(false)}
        />
        <style>
          {`
          .for-style .ag-row-focus {
              z-index: 2;
        }`}
        </style>
    </>
  );
};

export default AiKeywords;
